import { createSlice } from '@reduxjs/toolkit'
import { fetchCfsReports, fetchCfsReportStatus, fetchBeginningBalances, fetchMMMSplitSavedEntries, regenerateCfsReports, fetchGeneralLedgerData, fetchRawJournalData } from './thunks/cashFlowStatement';
import { cfsUniqueWPHandler, updatedCfsUniqueWPHandler } from '../../../utils/helperFunctions';
import { addPeriodInData } from '../../../pages/cfs/utils/helperFunctions';

const initialState = {
    generalLedgerData: [],
    isLoading: false,
    error: null,
    journalRawData: {
      periodOne: null,
      periodTwo: null
    },
    cfsWpMergedData: null,
    periodOneJournalDataRaw: null,
    periodTwoJournalDataRaw: null,
    periodOneJournalData: null,
    periodTwoJournalData: null,
    periodOneUniqueData: null,
    periodTwoUniqueData: null,
    peirodUniqueData: null,
    cfsFinalIndrectJsonPeriodOne: null,
    cfsFinalIndrectJsonPeriodTwo: null,
    cfsFinalDirectJsonPeriodOne: null,
    cfsFinalDirectJsonPeriodTwo: null,
    beginningBalances: null,
    MMMSplitSavedEntries: null,
    cfsSplitSearchEntry : {
      indirectCaption: "",
      period: "",
      method: "indirect",
      prefix: "",
    },
    periodOneType: null,
    periodTwoType: null,
    cfsPeriodOneTotals: null,
    cfsPeriodTwoTotals: null,
    cfsGroupingLibraryData: [],
    // Whether each period has a CFS worksheet, without the worksheet (see fetchCfsReportStatus).
    reportStatus: { periodOne: false, periodTwo: false }
}

const cashFlowStatement = createSlice({
    name: 'CASHFLOWSLICE',
    initialState,
    reducers: {
        addSymbols(state, action){
            state.availableSymbols = action.payload
        },
        allAndUniqueEntriesToggler(state, action){
          if(action.payload){
            state.periodOneJournalData = state.periodOneUniqueData;
            state.periodTwoJournalData = state.periodTwoUniqueData;
          }else{
            state.periodOneJournalData = state.periodOneJournalDataRaw;
            state.periodTwoJournalData = state.periodTwoJournalDataRaw;
          }
        },
        updateImDmHandler(state, action){
          if(action.payload.methodType === 'indirectMethod'){
            if(action.payload.period === "periodOne") state.cfsFinalIndrectJsonPeriodOne = action.payload.report;
            if(action.payload.period === "periodTwo") state.cfsFinalIndrectJsonPeriodTwo = action.payload.report;
          }
          
          if(action.payload.methodType === 'directMethod'){
            if(action.payload.period === "periodOne") state.cfsFinalDirectJsonPeriodOne = action.payload.report;
            if(action.payload.period === "periodTwo") state.cfsFinalDirectJsonPeriodTwo = action.payload.report;
          }
          
        },

        filterSplitViewData(state, action){
          state.cfsSplitSearchEntry.indirectCaption = action.payload.indirectCaption || ""
          state.cfsSplitSearchEntry.method = action.payload.method || "indirect"
          state.cfsSplitSearchEntry.period = action.payload.period || ""
          state.cfsSplitSearchEntry.prefix = action.payload.prefix || ""
        },

        setCfsGroupingLibraryData(state, action){
          state.cfsGroupingLibraryData = action.payload;
        }

    },
    extraReducers: (builder) => {
        builder.addCase(fetchBeginningBalances.pending, (state) => {
          state.isLoading = true
        })
        builder.addCase(fetchBeginningBalances.fulfilled, (state, action) => {
          state.isLoading = false;
          state.beginningBalances = action.payload || null;
          // state.generalLedgeData = action.payload
        })
        builder.addCase(fetchBeginningBalances.rejected, (state, action) => {
          state.isLoading = false 
          state.error = action.error.message
        })

        builder.addCase(fetchMMMSplitSavedEntries.pending, (state) => {
          state.isLoading = true
        })
        builder.addCase(fetchMMMSplitSavedEntries.fulfilled, (state, action) => {
          state.isLoading = false;
          state.MMMSplitSavedEntries = action.payload || null;
          // state.generalLedgeData = action.payload
        })
        builder.addCase(fetchMMMSplitSavedEntries.rejected, (state, action) => {
          state.isLoading = false 
          state.error = action.error.message
        })

        // Leaves isLoading alone on purpose: DefaultLayout shows its full-screen loader while
        // isLoading is set, and this lightweight check must never hold a page behind it.
        builder.addCase(fetchCfsReportStatus.fulfilled, (state, action) => {
          state.reportStatus = {
            periodOne: Boolean(action.payload?.periodOne),
            periodTwo: Boolean(action.payload?.periodTwo),
          };
        })

        builder.addCase(fetchCfsReports.pending, (state) => {
            state.isLoading = true
        })
        builder.addCase(fetchCfsReports.fulfilled, (state, action) => {
            // state.savedCfsIndirectTemplateJson = action.payload
            state.isLoading = false;
            state.cfsWpMergedData= action.payload.cfsWpMergedData;
            if(action.payload?.cfsWpTemplates[0]) {
              state.periodOneJournalData = addPeriodInData(action.payload.cfsWpTemplates[0].CFSWPTemp, action.payload.cfsWpTemplates[0].periodType);
              state.periodOneJournalDataRaw = action.payload.cfsWpTemplates[0].CFSWPTemp;
              state.periodOneType = action.payload.cfsWpTemplates[0].periodType;
              state.cfsPeriodOneTotals = action.payload.cfsWpTemplates[0].totals;
            } else {
              state.periodOneJournalData = null;
              state.periodOneJournalDataRaw = null;
              state.cfsPeriodOneTotals = null;
            }
            if(action.payload?.cfsWpTemplates[1]) {
              state.periodTwoJournalData = addPeriodInData(action.payload.cfsWpTemplates[1].CFSWPTemp, action.payload.cfsWpTemplates[1].periodType);
              state.periodTwoJournalDataRaw = action.payload.cfsWpTemplates[1].CFSWPTemp;
              state.periodTwoType = action.payload.cfsWpTemplates[1].periodType;
              state.cfsPeriodTwoTotals = action.payload.cfsWpTemplates[1].totals;
            } else {
              state.periodTwoJournalData = null;
              state.periodTwoJournalDataRaw = null;
              state.cfsPeriodTwoTotals = null;
            }
            
            if(action.payload?.cfsImTemplates[0]) {
              state.cfsFinalIndrectJsonPeriodOne = action.payload.cfsImTemplates[0].IndirectTemplate;
            } else {
              state.cfsFinalIndrectJsonPeriodOne = null
            }
            if(action.payload?.cfsImTemplates[1]) {
              state.cfsFinalIndrectJsonPeriodTwo = action.payload.cfsImTemplates[1].IndirectTemplate;
            } else {
              state.cfsFinalIndrectJsonPeriodTwo = null
            }
            
            if(action.payload?.cfsDmTemplates[0]) {
              state.cfsFinalDirectJsonPeriodOne = action.payload.cfsDmTemplates[0].directTemplate;
            } else {
              state.cfsFinalDirectJsonPeriodOne = null;
            }
            if(action.payload?.cfsDmTemplates[1]) {
              state.cfsFinalDirectJsonPeriodTwo = action.payload.cfsDmTemplates[1].directTemplate;
            }  else {
              state.cfsFinalDirectJsonPeriodTwo = null;
            }

            if(action.payload?.cfsWpTemplates[0]) {
              state.periodOneUniqueData = cfsUniqueWPHandler(action.payload.cfsWpTemplates[0].CFSWPTemp);
            } else {
              state.periodOneUniqueData = null;
            }
            if(action.payload?.cfsWpTemplates[1]) {
              state.periodTwoUniqueData = cfsUniqueWPHandler(action.payload.cfsWpTemplates[1].CFSWPTemp);
            } else {
              state.periodTwoUniqueData = null;
            }
            if(action.payload?.cfsWpTemplates[0] && action.payload?.cfsWpTemplates[1]) {
              state.peirodUniqueData = updatedCfsUniqueWPHandler([...addPeriodInData(action.payload.cfsWpTemplates[0]?.CFSWPTemp, action.payload.cfsWpTemplates[0]?.periodType), ...addPeriodInData(action.payload.cfsWpTemplates[1].CFSWPTemp, action.payload.cfsWpTemplates[1].periodType)], action.payload.cfsWpTemplates[0].periodType)
            } else {
              state.peirodUniqueData = updatedCfsUniqueWPHandler([...addPeriodInData(action.payload.cfsWpTemplates[0]?.CFSWPTemp, action.payload.cfsWpTemplates[0]?.periodType)], action.payload.cfsWpTemplates[0].periodType)
            }

        })
        builder.addCase(fetchCfsReports.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error.message
          
        })
        
        builder.addCase(regenerateCfsReports.pending, (state) => {
            state.isLoading = true
        })
        builder.addCase(regenerateCfsReports.fulfilled, (state, action) => {
            state.isLoading = false;
            // state.data = action.payload;
            // state.savedCfsIndirectTemplateJson = action.payload
            state.cfsWpMergedData= action.payload.cfsWpMergedData;
            if(action.payload?.periodOne && Object.keys(action.payload?.periodOne)?.length > 0) {
              state.periodOneJournalData = addPeriodInData(action.payload.periodOne.finalJournalData, state.periodOneType);
              state.periodOneJournalDataRaw = action.payload.periodOne.finalJournalData;
              state.cfsFinalIndrectJsonPeriodOne = action.payload.periodOne.indirectData;
              state.cfsFinalDirectJsonPeriodOne = action.payload.periodOne.directData;
              state.periodOneUniqueData = cfsUniqueWPHandler(addPeriodInData(action.payload.periodOne.finalJournalData, state.periodOneType), state.periodOneType);
            }
            if(action.payload?.periodTwo && Object.keys(action.payload?.periodTwo)?.length > 0) {
              state.periodTwoJournalData = addPeriodInData(action.payload.periodTwo.finalJournalData, state.periodTwoType, "unique");
              state.periodTwoJournalDataRaw =  action.payload.periodTwo.finalJournalData;
              state.cfsFinalIndrectJsonPeriodTwo = action.payload.periodTwo.indirectData;
              state.cfsFinalDirectJsonPeriodTwo = action.payload.periodTwo.directData;
              state.periodTwoUniqueData = cfsUniqueWPHandler(addPeriodInData(action.payload.periodTwo.finalJournalData, state.periodTwoType, "unique"), state.periodOneType);
            }
            if((action.payload?.periodOne && Object.keys(action.payload?.periodOne)?.length > 0) && (action.payload?.periodTwo && Object.keys(action.payload?.periodTwo)?.length > 0)) {
              state.peirodUniqueData = updatedCfsUniqueWPHandler([...addPeriodInData(action.payload.periodOne.finalJournalData, state.periodOneType), ...addPeriodInData(action.payload.periodTwo.finalJournalData, state.periodTwoType, "unique")], state.periodOneType)
            } else {
              state.peirodUniqueData = updatedCfsUniqueWPHandler([...addPeriodInData(action.payload.periodOne.finalJournalData, state.periodOneType)], state.periodOneType)
            }

        })
        builder.addCase(regenerateCfsReports.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error.message
        })

        builder.addCase(fetchGeneralLedgerData.pending, (state) => {
          	state.isLoading = true
        })
        builder.addCase(fetchGeneralLedgerData.fulfilled, (state, action) => {
          state.isLoading = false;
          state.generalLedgerData = action.payload || null;
        })
        builder.addCase(fetchGeneralLedgerData.rejected, (state, action) => {
          state.isLoading = false 
          state.error = action.error.message
        })
        
        builder.addCase(fetchRawJournalData.pending, (state) => {
          	state.isLoading = true
        })
        builder.addCase(fetchRawJournalData.fulfilled, (state, action) => {
          state.isLoading = false;
          state.journalRawData.periodOne = action.payload?.periodOne || null;
          state.journalRawData.periodTwo = action.payload?.periodTwo || null;
        })
        builder.addCase(fetchRawJournalData.rejected, (state, action) => {
          state.isLoading = false 
          state.error = action.error.message
        })
    }
});

export const { addSymbols, allAndUniqueEntriesToggler, updateImDmHandler, filterSplitViewData, setCfsGroupingLibraryData } = cashFlowStatement.actions

export default cashFlowStatement;